import type * as THREE from 'three';
import type { BeatSequencer } from '@/story/sequencer';
import type { Gait, SpawnPoint } from '@/physics/character';
import type { Bloom, BloomOptions } from '@/render/bloom';
import type { ZonePalette } from '@/render/palette';
import type { RoomId } from '@/story/beats';
import { validateInteractables, type Interactable } from '@/systems/interaction';
import type { BeatFadeCues } from '@/world/beatFadeDirector';
import type { BornDebug } from '@/world/bornDirector';
import type { Framing } from '@/camera/framing';
import type { TraceExternalEvent } from '@/core/trace';
import type { PlanMark } from '@/ui/mentalMap';
import type { OutDebug } from '@/game/hedgerowOut';
import type { TwelveOutDebug } from '@/game/hedgerowNight';
import type { BlackoutHallDebug } from '@/world/blackoutHallDirector';
import type { OttoDebug } from '@/world/ottoDirector';
import type { ExposureBreakdown, HasLineOfSight, StealthSystem } from '@/systems/stealth';
import type { YardDebug } from '@/world/yardDirector';
import type { ColliderHandle, RoomSeams } from '@/game/roomModule';
import type { HallSolid } from '@/world/solids';
import type { CorridorDebug } from '@/world/corridorDirector';
import type { LatchDebug } from '@/world/latchDirector';
import type { CullDebug } from '@/world/cullDirector';

/**
 * The host borrows this record for one fixed call only. A module may inspect
 * `player`, but must not retain it: `main.ts` mutates the same scratch object
 * on the next tick to avoid allocating in the simulation hot path.
 */
export interface BeatPartTick {
  readonly dt: number;
  readonly player: { readonly x: number; readonly y: number; readonly z: number };
  readonly raiseFlag: (flag: string) => void;
  /** Whether a story flag is up — the room's as well as this part's. */
  readonly hasFlag: (flag: string) => boolean;
  /**
   * Put her HERE now: `character.reset`, applied inside the call, once. Not
   * `RoomModule.carry`, which holds her every tick — this is a placement she
   * walks on from (beat 7's backstops and capture). The host always supplies
   * it; optional only so a sim-lane tick for a part that never moves her can
   * stay four fields.
   */
  readonly moveSelf?: (to: SpawnPoint) => void;
  /** She nudged this interactable on THIS tick — an edge, as `RoomTick.nudged`. */
  readonly nudged?: (interactableId: string) => boolean;
  /**
   * She finished a root on this interactable on THIS tick. An edge, because a
   * `once: false` target completes again and again (beat 7's boards) and
   * `completed` would latch.
   */
  readonly rooted?: (interactableId: string) => boolean;
  /** `F` was pressed on THIS tick — an edge, as `RoomTick.called`. */
  readonly called?: boolean;
  /**
   * Her gait and footing after this tick's controller, and how far this tick
   * actually moved her (`current - previous`, not `speed * dt`: a roll
   * grinding along a wall has speed and no ground). Beat 3's roll-in-straw
   * distance; beat 4's scent trail reads `grounded`. `speed` is the
   * controller's, for a stride counted as `speed * dt` the way the host's own
   * footfalls are (beat 2's, which the worker hears). Filled for `afterMove`
   * and `fixedStep`; `moveSelf` zeroes the displacement. Optional so a
   * sim-lane tick that never reads it stays four fields.
   */
  readonly motion?: { readonly gait: Gait; readonly speed: number; readonly grounded: boolean; readonly dx: number; readonly dz: number };
}

/**
 * Her simulation state as the previous fixed step left it, for
 * `BeatPartModule.beforeMove`. Read-only and borrowed for the call.
 */
export interface BeatPartMoveState {
  readonly x: number;
  readonly z: number;
  readonly facing: number;
  readonly speed: number;
  readonly gait: string;
}

/** A looping sound a part started. The host's `AudioHandle`, narrowed. */
export interface BeatPartLoop {
  /** False for the inert handle `playLoop` returns before the context unlocks. */
  readonly active: boolean;
  setPosition(position: { x: number; y: number; z: number }): void;
  setGain(gain: number, rampSeconds?: number): void;
  /** Beat 5's castors, whose pitch follows the bin's speed. */
  setPlaybackRate(rate: number, rampSeconds?: number): void;
  stop(fadeSeconds?: number): void;
}

/**
 * The world a part that stages things needs: the scene, sound, the bloom, the
 * interaction system and, per room, its seams. Optional as a whole because the
 * parts before beat 7's staged nothing and their sim-lane specs build with
 * `createFade` alone; a part that needs it throws at `create` without it.
 */
export interface BeatPartWorld {
  readonly scene: THREE.Scene;
  readonly playOneShot: (id: string, options: { gain: number; position: { x: number; y: number; z: number }; detune?: number }) => void;
  readonly playLoop: (id: string, options: { gain: number; position?: { x: number; y: number; z: number } }) => BeatPartLoop;
  /** Beat 7's bloom (`Bloom`), with the host's post stack and scene already bound. */
  readonly createBloom: (from: ZonePalette, to: ZonePalette, options: Omit<BloomOptions, 'post' | 'scene'>) => Bloom;
  /**
   * `moveTo` re-points an interactable at a moving subject (beat 4's patrol
   * under `observe`); call it from `afterMove` so this tick's sampling sees it.
   */
  readonly interaction: {
    setEnabled(id: string, enabled: boolean): void;
    moveTo(id: string, at: { x: number; y: number; z: number }): void;
    /**
     * Whether a hold on this interactable has completed. Read from `afterMove`
     * it is LAST tick's answer, before this tick's sampling — beat 6's
     * `watchedHim` has always read it there.
     */
    completed(id: string): boolean;
  };
  /**
   * Colliders the PART owns: beat 4's door leaf, which swings, so its
   * collider follows the mesh (`setPose`, on the fixed step). The room's
   * handle type; a part removes what it added on `dispose`.
   */
  readonly colliders: {
    add(solid: HallSolid): ColliderHandle;
    setPose(handle: ColliderHandle, pose: { readonly position: { x: number; y: number; z: number }; readonly quaternion: { x: number; y: number; z: number; w: number } }): void;
    remove(handle: ColliderHandle): void;
  };
  /**
   * Stop the room's ambience beds now, not on the engine's walk-out fade:
   * beat 8's fuse, whose sound carries the last of the hum and then silence.
   */
  readonly cutAmbience: () => void;
  /**
   * The world's half of a part's stealth field: can this eye reach that point?
   * A Rapier `castRay`, so it is only true to the colliders after the host's
   * first `physics.step` — which is why a part steps its watchers in
   * `afterPhysics` (beat 10's dog).
   */
  readonly lineOfSight: HasLineOfSight;
  /**
   * The same ray as `lineOfSight`, answering how far along `from → to` the
   * first collider that is not hers sits, or null when nothing is in the way.
   * For a part whose sight rule is not the shared one: beat 2 forgives a hit
   * within 2 cm of her. Same Rapier caveat — true only after the first step.
   */
  readonly rayHit: (from: { x: number; y: number; z: number }, to: { x: number; y: number; z: number }) => number | null;
  /**
   * A lightning strike's three sinks (D-107), set together on the render
   * step: the post stack's storm flash (0..1), a multiplier on the room rig's
   * AUTHORED hemisphere intensity, and `scene.backgroundIntensity`. Beat 6's
   * storm; the gains that turn one flash into three numbers are the part's.
   */
  readonly stormFlash: (flash: number, hemiScale: number, backgroundIntensity: number) => void;
  /**
   * Run `effect` at the very start of the next fixed tick — before a pending
   * teleport, pause and the controller — and record it in the golden trace as
   * `kind`. For a physics mutation a part decides OFF the fixed step (beat 1's
   * director takes the littermate on the render step, and the render step
   * must never touch Rapier). One effect per kind per tick: a second request
   * before the tick replaces the first, as the host's latch did.
   */
  readonly atNextTick: (kind: Exclude<TraceExternalEvent['kind'], 'teleportPlayer'>, effect: () => void) => void;
  /** The room's typed handles (`RoomSeams`), passed through by `roomSeamsOf`. */
  readonly room: RoomSeams;
}

export interface BeatFade {
  readonly fade: number;
  handle(cue: { id: string }): void;
  update(frameDt: number): void;
  dispose(): void;
}

export interface BeatPartCapabilities {
  createFade(cues: BeatFadeCues): BeatFade;
  readonly world?: BeatPartWorld;
}

export interface BeatPartModule {
  readonly story: BeatSequencer;
  readonly interactables: readonly Interactable[];
  /** 0..1, what the hand-over waits on (>= 0.99 navigates). A number: the overlay stays the module's. */
  readonly fade: number;
  /** True while the part owns the frame and the verb prompt must hide. */
  readonly promptSuppressed: boolean;
  fixedStep(tick: BeatPartTick): void;
  handleCue(cue: { id: string }): void;
  /**
   * `her` is her INTERPOLATED render position, borrowed for the call and
   * mutated by the host afterwards (beat 8's torch turns to it; the sim tick's
   * `player` is a fixed-step position and would judder at display rate).
   */
  renderStep(frameDt: number, her: { readonly x: number; readonly y: number; readonly z: number }): void;
  /**
   * The beat's authored shot, or null to leave the lens to the room and the
   * rig. Read every render step; the host eases into and out of it, as it does
   * a room's observe framing. `her` is her interpolated render position,
   * borrowed as for `renderStep` (beat 1's opening frames her face). Optional:
   * most parts never hold the camera.
   */
  framing?(her: { readonly x: number; readonly y: number; readonly z: number }): Framing | null;
  /**
   * True when `framing()` CUTS rather than eases, as the legacy directors'
   * shots always have. Beat 7's cameras cut, and an eased version of the same
   * shot is a different shot. Optional; absent means eased.
   */
  readonly framingCuts?: boolean;
  /** Marks drawn AHEAD of her on her sheet — beat 12's plan. Optional. */
  planMarks?(): readonly PlanMark[];
  /**
   * Called BEFORE the controller moves her this tick, with last tick's state:
   * beat 7's boards must break on the arriving roll, because once Rapier has
   * met the intact collider she reports no speed. Optional.
   */
  beforeMove?(state: BeatPartMoveState, raiseFlag: (flag: string) => void): void;
  /**
   * After the controller has moved her and BEFORE interaction sampling: a
   * part whose subjects move on the fixed step and must be where the
   * interaction system looks this tick (beat 4's patrols under `observe`,
   * its door collider). `tick.moveSelf` may be called here and later
   * (beat 4's capture respawns her from inside its step). Optional.
   */
  afterMove?(tick: BeatPartTick): void;
  /**
   * After the story has read this tick's flags and before `physics.step`:
   * beat 10's companion and the bell she rings into the dog's stealth field.
   * `player` is where she stands now, after the controller and any placement.
   * Optional.
   */
  beforePhysics?(tick: BeatPartTick): void;
  /**
   * After `physics.step`, because a watcher's line of sight is a Rapier ray
   * and Rapier rebuilds its query structure inside the step (beat 10's dog,
   * then the queue that must read where a capture put her). Optional.
   */
  afterPhysics?(tick: BeatPartTick): void;
  /**
   * The render step AFTER the audio listener has moved: sounds decided on the
   * fixed step and positioned in the world (beat 10's bell strokes). Optional.
   */
  renderLate?(frameDt: number): void;
  /**
   * The part's stealth field, when it builds its own watchers. The host feeds
   * it her footfalls and reports it; the part steps it. Optional.
   */
  readonly stealth?: StealthSystem;
  /** What `F` will do next, for the touch button and the one-shot teach. Optional. */
  readonly callVerb?: 'call' | 'send';
  /** `GameHandle.respawn`: back to the part's start, whatever it keeps. Optional. */
  resetForRespawn?(): void;
  /** What the section boundary writes beside the ledger (`commitBoundary`). Optional. */
  outcome?(): { readonly out?: number };
  /** What the test seam reads for this part (`window.__rosalie`). Optional. */
  diagnostics?(player: { readonly x: number; readonly y: number; readonly z: number }): BeatPartDiagnostics;
  dispose(): void;
}

/** One member per part that reports; the host routes each to its own getter. */
export type BeatPartDiagnostics =
  | { readonly kind: 'out'; readonly out: OutDebug }
  | { readonly kind: 'twelve-out'; readonly twelveOut: TwelveOutDebug }
  | { readonly kind: 'blackout-hall'; readonly blackout: BlackoutHallDebug }
  | { readonly kind: 'otto'; readonly otto: OttoDebug }
  | { readonly kind: 'corridor'; readonly corridor: CorridorDebug }
  | { readonly kind: 'latch'; readonly latch: LatchDebug }
  | { readonly kind: 'cull'; readonly cull: CullDebug }
  | { readonly kind: 'born'; readonly born: BornDebug }
  /**
   * `exposureAt` is a query, not a reading: beat 6's flash gate samples the
   * LIVE decorated grid at points it chooses (`yardExposureAt`).
   */
  | { readonly kind: 'yard'; readonly yard: YardDebug; exposureAt(x: number, z: number): ExposureBreakdown };

export interface BeatPartModuleDefinition {
  readonly beat: number;
  readonly part: number;
  readonly room: RoomId;
  create(capabilities: BeatPartCapabilities): BeatPartModule;
}

/** Fail at boot with the authored part named, before a malformed module can tick. */
export function validateBeatPartModule(module: BeatPartModule, definition: BeatPartModuleDefinition): void {
  const context = `beat ${definition.beat} / part ${definition.part} / ${definition.room}`;
  const required: readonly (keyof BeatPartModule)[] = [
    'story', 'interactables', 'fade', 'promptSuppressed', 'fixedStep', 'handleCue', 'renderStep', 'dispose',
  ];
  for (const field of required) {
    if (module[field] === undefined || module[field] === null) {
      throw new Error(`${context}: module is missing required field "${field}"`);
    }
  }
  if (typeof module.story.update !== 'function' || typeof module.story.drainCues !== 'function') {
    throw new Error(`${context}: story must provide update() and drainCues()`);
  }
  if (module.story.beatNumber !== definition.beat) {
    throw new Error(`${context}: module story is beat ${module.story.beatNumber}`);
  }
  if (typeof module.fixedStep !== 'function' || typeof module.handleCue !== 'function'
    || typeof module.renderStep !== 'function' || typeof module.dispose !== 'function') {
    throw new Error(`${context}: module lifecycle fields must be functions`);
  }
  if (typeof module.fade !== 'number' || !Number.isFinite(module.fade)) {
    throw new Error(`${context}: fade must be a finite number`);
  }
  if (typeof module.promptSuppressed !== 'boolean') {
    throw new Error(`${context}: promptSuppressed must be boolean`);
  }
  validateInteractables(module.interactables, context);
}
